import { computed, effect, inject, Injectable, signal, TemplateRef, untracked } from '@angular/core';
import { EditDataService } from './_edit-data.service';

/** Quem abre o painel: o menu Foto, Grade ou Vídeo do rail de um card. */
export interface MediaLookPanelRequest {
    /** Dono do painel: uma instância do menu (cada card tem o seu). */
    ownerId: string;
    /** O item sendo ajustado: a prévia do PDF rola até ele. */
    itemId?: string | null;
    /** Título e ícone do cabeçalho, os mesmos do botão do rail. */
    titleKey: string;
    icon: string;
    /** O conteúdo: um template do próprio menu, que continua ligado ao card que o abriu. */
    template: TemplateRef<unknown>;
}

/**
 * Fases do painel:
 *
 * - `enter`: o painel entra deslizando pela esquerda e o fundo escurece;
 * - `open`: aberto;
 * - `leave`: o painel sai pela esquerda e o fundo clareia. A prévia, se foi o painel que a
 *   abriu, fecha só no fim, para a coluna não mexer por baixo do fundo ainda escuro.
 */
export type MediaLookPanelPhase = 'closed' | 'enter' | 'open' | 'leave';

/** Tempo do deslize do painel e do fundo (media-look-panel.component.scss). */
const ENTER_MS = 280;
const LEAVE_MS = 280;

/**
 * Painel de formato das mídias (tamanho, enquadramento, legenda, número e data), em modo foco.
 *
 * Ajustar a foto é ajustar como ela sai no PDF, então o que interessa durante o ajuste são só
 * duas coisas: as opções e a prévia. O painel abre na ESQUERDA, por cima do editor escurecido
 * (como um modal), e a prévia fica acesa na coluna da direita, rolada até o item: cada clique
 * aparece na folha. Com a prévia fechada, o painel a abre e a fecha de novo quando sai.
 *
 * Trocar de card com o painel aberto (o "Configurações deste item" de outro item na prévia)
 * só troca o conteúdo e rola a prévia até o novo item.
 */
@Injectable({ providedIn: 'root' })
export class MediaLookPanelService {
    private _data = inject(EditDataService);

    private _request = signal<MediaLookPanelRequest | null>(null);
    private _phase = signal<MediaLookPanelPhase>('closed');
    /** A prévia estava fechada quando o painel abriu: ela fecha de novo quando ele sair. */
    private _closePreviewOnExit = false;
    private _timer: ReturnType<typeof setTimeout> | undefined;

    public request = this._request.asReadonly();
    public phase = this._phase.asReadonly();

    /** Aberto ou abrindo: é o que vale para as decisões (clique fora, trocar de card). */
    public focused = computed(() => {
        const phase = this._phase();
        return phase === 'enter' || phase === 'open';
    });

    /**
     * Fundo escuro na tela e prévia por cima dele. Inclui a saída: a prévia só desce para
     * debaixo do fundo quando ele já sumiu, senão ela escureceria no meio da animação.
     */
    public active = computed(() => this._phase() !== 'closed');

    /** Aberto (ou abrindo) para este dono: é o que acende o botão do rail. */
    public isOpenFor(ownerId: string): boolean {
        return this._request()?.ownerId === ownerId && this.focused();
    }

    constructor() {
        // A prévia fechou por fora com o painel aberto (o ✕ dela): sem a folha não há o que
        // acompanhar, então o painel sai junto, e a prévia fica como a pessoa deixou.
        effect(() => {
            const previewOpen = this._data.pdfPreviewOpen();
            untracked(() => {
                if (previewOpen || !this.focused()) return;
                this._closePreviewOnExit = false;
                this._leave();
            });
        });
    }

    /** Abre o painel com o conteúdo de um card, ou troca o conteúdo se ele já está aberto. */
    public open(request: MediaLookPanelRequest): void {
        if (this.focused()) {
            this._request.set(request);
            this._locate(request);
            return;
        }

        // Fechado, ou fechando no meio da animação: a prévia na tela agora decide se ela fecha
        // de novo na saída. Reaberto no meio da saída, vale o que foi decidido na abertura.
        this._clearTimer();
        if (this._phase() === 'closed') this._closePreviewOnExit = !this._data.pdfPreviewOpen();
        this._data.openPdfPreview();
        this._request.set(request);
        this._phase.set('enter');
        this._data.setMediaPanelOpen(true);
        this._locate(request);

        this._after(ENTER_MS, () => this._phase.set('open'));
    }

    /** Fecha o painel. Com `ownerId`, só fecha se for de quem pediu (um card que saiu da tela). */
    public close(ownerId?: string): void {
        const request = this._request();
        if (!request || (ownerId && request.ownerId !== ownerId)) return;
        if (!this.focused()) return;

        this._leave();
    }

    /**
     * O card dono saiu da tela (outra seção, campo removido). Espera uma volta antes de
     * fechar: trocar de item pela prévia para um que mora noutra seção desmonta o card de
     * antes logo ANTES de o novo pedir o painel, e fechar na hora faria o painel sair e
     * entrar de novo, com o fundo piscando.
     */
    public release(ownerId: string): void {
        setTimeout(() => this.close(ownerId));
    }

    /** A prévia rola até o item e o destaca (o mesmo "Encontrar na prévia" do card). */
    private _locate(request: MediaLookPanelRequest): void {
        if (request.itemId) this._data.requestPreviewScroll(request.itemId);
    }

    private _leave(): void {
        this._clearTimer();
        this._phase.set('leave');

        this._after(LEAVE_MS, () => {
            if (this._closePreviewOnExit) this._data.closePdfPreview();
            this._closePreviewOnExit = false;
            this._phase.set('closed');
            this._request.set(null);
            this._data.setMediaPanelOpen(false);
        });
    }

    private _after(ms: number, run: () => void): void {
        this._clearTimer();
        this._timer = setTimeout(() => {
            this._timer = undefined;
            run();
        }, prefersReducedMotion() ? 0 : ms);
    }

    private _clearTimer(): void {
        if (this._timer) clearTimeout(this._timer);
        this._timer = undefined;
    }
}

function prefersReducedMotion(): boolean {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
